// Shared math helpers. Pure functions only — no three.js imports here so this
// module stays cheap to pull into workers and tests.

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v) => clamp(v, 0, 1);
export const lerp = (a, b, t) => a + (b - a) * t;
export const invLerp = (a, b, v) => (b - a === 0 ? 0 : (v - a) / (b - a));
export const remap = (v, a, b, c, d) => lerp(c, d, clamp01(invLerp(a, b, v)));
export const smoothstep = (t) => { t = clamp01(t); return t * t * (3 - 2 * t); };
export const smootherstep = (t) => { t = clamp01(t); return t * t * t * (t * (t * 6 - 15) + 10); };

/**
 * Framerate-independent exponential approach. `rate` is the fraction of the
 * remaining distance covered per second, so damp(a, b, 0.9, dt) closes 90% of
 * the gap every second regardless of dt.
 */
export const damp = (a, b, rate, dt) => lerp(a, b, 1 - Math.pow(1 - rate, dt * 60));
export const expDamp = (a, b, lambda, dt) => lerp(a, b, 1 - Math.exp(-lambda * dt));

/** Critically damped spring toward `target`. Returns [value, velocity]. */
export function springDamp(value, target, velocity, smoothTime, dt, maxSpeed = Infinity) {
  const omega = 2 / Math.max(1e-4, smoothTime);
  const x = omega * dt;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  let change = value - target;
  const maxChange = maxSpeed * smoothTime;
  change = clamp(change, -maxChange, maxChange);
  const temp = (velocity + omega * change) * dt;
  const newVel = (velocity - omega * temp) * exp;
  let newVal = target + change + (change * -1 + change + temp) * exp;
  // guard against overshoot past the target
  if ((target - value > 0) === (newVal > target)) { newVal = target; }
  return [newVal, newVel];
}

export const shortestAngle = (a, b) => {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
};

// ---------------------------------------------------------------------------
// Deterministic randomness. Every system that needs noise takes a seeded RNG so
// a given level/loadout renders identically across reloads and screenshots.
// ---------------------------------------------------------------------------

/** mulberry32 — small, fast, good enough distribution for gameplay + textures. */
export function makeRng(seed = 0x9e3779b9) {
  let a = seed >>> 0;
  const rng = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  rng.range = (lo, hi) => lo + (hi - lo) * rng();
  rng.int = (lo, hi) => Math.floor(lo + (hi - lo + 1) * rng());
  rng.pick = (arr) => arr[Math.floor(rng() * arr.length) % arr.length];
  rng.sign = () => (rng() < 0.5 ? -1 : 1);
  /** Box-Muller, unit variance. */
  rng.gauss = () => {
    const u = Math.max(1e-9, rng());
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(TAU * rng());
  };
  return rng;
}

export function hash2(x, y) {
  let h = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Value noise with smootherstep interpolation. Domain is world units. */
export function valueNoise2(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = smootherstep(x - xi), yf = smootherstep(y - yi);
  const a = hash2(xi, yi), b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1);
  return lerp(lerp(a, b, xf), lerp(c, d, xf), yf);
}

export function fbm2(x, y, octaves = 5, lacunarity = 2.0, gain = 0.5) {
  let amp = 0.5, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise2(x * freq, y * freq);
    norm += amp;
    amp *= gain; freq *= lacunarity;
  }
  return sum / norm;
}

/** Ridged multifractal — good for rust streaks, cracked concrete, scratches. */
export function ridge2(x, y, octaves = 4) {
  let amp = 0.5, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(valueNoise2(x * freq, y * freq) * 2 - 1);
    sum += amp * n * n;
    norm += amp;
    amp *= 0.5; freq *= 2;
  }
  return sum / norm;
}

/** Worley/cellular F1 distance in [0,1]. Used for gravel, chipped paint, sparks. */
export function worley2(x, y, jitter = 1) {
  const xi = Math.floor(x), yi = Math.floor(y);
  let best = 8;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const cx = xi + ox, cy = yi + oy;
      const px = cx + hash2(cx, cy) * jitter;
      const py = cy + hash2(cy, cx * 31) * jitter;
      const dx = px - x, dy = py - y;
      const d = dx * dx + dy * dy;
      if (d < best) best = d;
    }
  }
  return clamp01(Math.sqrt(best));
}

// ---------------------------------------------------------------------------
// Small allocation-free helpers used in hot loops
// ---------------------------------------------------------------------------

/** Object pool. `make` builds, `reset` is called on release. */
export class Pool {
  constructor(make, reset, prealloc = 0) {
    this.make = make; this.reset = reset; this.free = [];
    for (let i = 0; i < prealloc; i++) this.free.push(make());
  }
  acquire() { return this.free.length ? this.free.pop() : this.make(); }
  release(o) { if (this.reset) this.reset(o); this.free.push(o); }
}

/** Rolling ring buffer of numbers — frame timings, damage history, etc. */
export class Ring {
  constructor(n) { this.buf = new Float32Array(n); this.n = n; this.i = 0; this.count = 0; }
  push(v) { this.buf[this.i] = v; this.i = (this.i + 1) % this.n; if (this.count < this.n) this.count++; }
  get avg() { let s = 0; for (let k = 0; k < this.count; k++) s += this.buf[k]; return this.count ? s / this.count : 0; }
  get max() { let m = -Infinity; for (let k = 0; k < this.count; k++) m = Math.max(m, this.buf[k]); return m; }
  percentile(p) {
    if (!this.count) return 0;
    const a = Array.from(this.buf.subarray(0, this.count)).sort((x, y) => x - y);
    return a[clamp(Math.floor(p * a.length), 0, a.length - 1)];
  }
}
